import { FolderOpen, UploadCloud } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MAX_FILES_PER_SELECTION } from '@flowshare/protocol';
import { filesFromDataTransfer } from '@/lib/file-transfer/file-picker';

interface DropZoneProps {
  onFiles: (files: File[]) => void;
  compact?: boolean;
  disabled?: boolean;
  className?: string;
  hint?: string;
}

export function DropZone({ onFiles, compact = false, disabled = false, className, hint }: DropZoneProps) {
  const [dragging, setDragging] = React.useState(false);
  const dragDepth = React.useRef(0);
  const fileInput = React.useRef<HTMLInputElement>(null);
  const folderInput = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    folderInput.current?.setAttribute('webkitdirectory', '');
    folderInput.current?.setAttribute('directory', '');
  }, []);

  const emit = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    onFiles(Array.from(files).slice(0, MAX_FILES_PER_SELECTION));
  };

  const onDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    if (disabled) return;
    dragDepth.current += 1;
    setDragging(true);
  };

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const onDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (disabled) return;
    const files = await filesFromDataTransfer(event.dataTransfer);
    if (files.length > 0) onFiles(files);
  };

  return (
    <div
      className={cn(
        'dropzone group relative flex flex-col items-center justify-center overflow-hidden rounded-2xl border border-dashed text-center transition-all duration-200',
        compact ? 'min-h-[132px] gap-2 p-5' : 'min-h-[330px] gap-4 p-8',
        dragging
          ? 'border-primary/70 bg-primary/[0.06] shadow-[0_0_0_1px_var(--ring),0_20px_60px_-30px_var(--glow)]'
          : 'border-border hover:border-foreground/20 hover:bg-muted/20',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
      data-dragging={dragging}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={(event) => void onDrop(event)}
    >
      <div
        className={cn(
          'pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent transition-opacity',
          dragging ? 'opacity-100' : 'opacity-0',
        )}
      />

      <span
        className={cn(
          'relative grid place-items-center rounded-2xl border border-border/70 bg-card/70 text-primary shadow-[0_10px_30px_-16px_var(--glow)] transition-transform duration-300',
          compact ? 'size-11 rounded-xl' : 'size-16',
          dragging ? 'scale-110' : 'group-hover:-translate-y-0.5',
        )}
      >
        <UploadCloud className={compact ? 'size-5' : 'size-7'} strokeWidth={1.6} />
      </span>

      <div className="space-y-1.5">
        <p className={cn('font-semibold tracking-[-0.02em]', compact ? 'text-sm' : 'text-xl')}>
          {dragging ? 'Release to add files' : 'Drop files here'}
        </p>
        <p className={cn('text-muted-foreground', compact ? 'text-[11px]' : 'text-sm')}>
          {hint ?? `or choose from this device · up to ${MAX_FILES_PER_SELECTION} files`}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          type="button"
          size={compact ? 'sm' : 'default'}
          variant={compact ? 'outline' : 'default'}
          onClick={() => fileInput.current?.click()}
        >
          <UploadCloud />
          Select files
        </Button>
        <Button
          type="button"
          size={compact ? 'sm' : 'default'}
          variant="outline"
          onClick={() => folderInput.current?.click()}
        >
          <FolderOpen />
          Select folder
        </Button>
      </div>

      {!compact ? (
        <p className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground/60">
          Files stream from disk in chunks · Nothing is uploaded
        </p>
      ) : null}

      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          emit(event.target.files);
          event.target.value = '';
        }}
      />
      <input
        ref={folderInput}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          emit(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
}
